import React, { useState, useEffect } from "react";
import { 
  Button, TextField, Card, CircularProgress, Typography, 
  Alert, Box, Dialog, DialogTitle, DialogContent, 
  DialogActions, ThemeProvider, createTheme, Paper, 
  Container, Chip, Divider, IconButton, useMediaQuery,
  Grid, CardContent, CardMedia, Avatar
} from "@mui/material";
import { saveAs } from "file-saver";
import axios from "axios";
import TagIcon from '@mui/icons-material/Tag';
import DownloadIcon from '@mui/icons-material/Download';
import FavoriteIcon from '@mui/icons-material/Favorite';
import ShareIcon from '@mui/icons-material/Share';
import CommentIcon from '@mui/icons-material/Comment';
import WebIcon from '@mui/icons-material/Web';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import CurrencyBitcoinIcon from '@mui/icons-material/CurrencyBitcoin';
import ReplyIcon from '@mui/icons-material/Reply';

// Create a custom theme with crypto colors
const theme = createTheme({
  palette: {
    primary: {
      main: '#F7931A', // Bitcoin orange
    },
    secondary: {
      main: '#627EEA', // Ethereum blue
    },
    background: {
      default: '#121212',
      paper: '#1E1E1E',
    },
    text: {
      primary: '#FFFFFF',
      secondary: '#AAAAAA',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 800,
      letterSpacing: '-0.025em',
    },
    h2: {
      fontWeight: 700,
    },
    h4: {
      fontWeight: 700,
    },
    button: {
      fontWeight: 600,
      textTransform: 'none',
    },
  },
  shape: {
    borderRadius: 12,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 30,
          padding: '10px 20px',
          fontSize: '0.9rem',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
          borderRadius: 16,
          backgroundColor: '#1E1E1E',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 12,
            color: '#FFFFFF',
          },
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(255, 255, 255, 0.2)',
          },
          '& .MuiInputLabel-root': {
            color: '#AAAAAA',
          },
        },
      },
    },
  },
});

export default function CryptoReplyGen() {
  const [tweetContent, setTweetContent] = useState("");
  const [targetCoin, setTargetCoin] = useState("APE OUT");
  const [loading, setLoading] = useState(false);
  const [imageLoading, setImageLoading] = useState(false);
  const [error, setError] = useState("");
  const [credits, setCredits] = useState(3); // Start with 3 free credits
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);
  const [replies, setReplies] = useState([]);
  const [generatedReply, setGeneratedReply] = useState("");
  const [generatedImage, setGeneratedImage] = useState("");
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // Sample pre-loaded replies
  const predefinedReplies = [
    {
      id: 1,
      originalTweet: "HOLY CRYPTO FAM... BUSTA RHYMES JUST TWEETED ABOUT BITCOIN! THE FLIPPENING IS COMING!",
      image: "https://i.imgur.com/El4oFRP.jpeg",
      replyText: "BUSTA BUSTIN' INTO CRYPTO! 🔥🚀 While BTC going sideways, $APEOUT pumping 24% today! The real GIMME SOME MORE energy! #CryptoFam #APEOUT",
      likes: 342,
      comments: 57,
      shares: 128
    },
    {
      id: 2,
      originalTweet: "We BUYIN THIS DIP? Or waiting for more blood? #BTC",
      image: "https://i.imgur.com/mDtMMrx.png",
      replyText: "While everyone panicking I'm loading $APEOUT bags 🦍💰 Up 3x since last week! THIS the real dip you should be buying ser! 🍌",
      likes: 789,
      comments: 125,
      shares: 246
    },
    {
      id: 3,
      originalTweet: "So Busta Rhymes is into #NFTs now? Celebrity adoption bullish AF",
      image: "https://i.imgur.com/hKeu68c.png",
      replyText: "BUSTA knows the REAL gems 💎 $APEOUT just launched Bored Rhymes NFT collection! Early minters get 2x token rewards! Don't sleep on this! 🦍",
      likes: 1204,
      comments: 203,
      shares: 512
    }
  ];

  // Load credits from localStorage on component mount
  useEffect(() => {
    const savedCredits = localStorage.getItem('crypto-reply-credits');
    if (savedCredits !== null) {
      setCredits(parseInt(savedCredits));
    }
    
    // Initialize with predefined replies
    setReplies(predefinedReplies);
  }, []);
  
  // Save credits to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('crypto-reply-credits', credits.toString());
  }, [credits]);

  const generateReplyWithOpenAI = async () => {
    if (!tweetContent.trim()) {
      setError("Please enter a tweet to reply to first.");
      return;
    }
    
    if (credits <= 0) {
      setShowUpgradeDialog(true);
      return;
    }

    setLoading(true);
    setError("");
    
    try {
      const model = "gpt-3.5-turbo";
      
      const prompt = `
        Create a crypto Twitter reply that responds to the tweet below. The reply should shill the coin "${targetCoin}" and make it sound like an exciting opportunity.
        
        Original Tweet: "${tweetContent}"
        
        Make the reply enthusiastic, using crypto slang, emojis, and typical "crypto reply guy" language. Include some of these elements:
        - Use terms like "ser", "fam", "ape in", "moon", "bullish"
        - Mention percentage gains or potential gains
        - Create FOMO (Fear Of Missing Out)
        - Use capital letters for emphasis
        - Include relevant hashtags
        - Keep it under 280 characters
        - Make it sound like you're an insider with special knowledge
        
        Be creative and slightly over-the-top as typical crypto Twitter replies are.
      `;
      
      const response = await axios.post(
        "https://api.openai.com/v1/chat/completions",
        {
          model: model,
          messages: [
            { role: "system", content: "You are an expert crypto Twitter marketer who specializes in creating engaging crypto replies." },
            { role: "user", content: prompt }
          ],
          temperature: 1.0,
          max_tokens: 150
        },
        {
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${process.env.REACT_APP_OPENAI_API_KEY}`
          }
        }
      );
      
      const generatedReplyText = response.data.choices[0].message.content.trim();
      setGeneratedReply(generatedReplyText);
      
      // Now generate the image
      generateImage(generatedReplyText);
      
      // Deduct a credit after successful generation
      setCredits(prevCredits => prevCredits - 1);
      
    } catch (err) {
      console.error("Error generating reply:", err);
      setError(
        err.response?.data?.error?.message || 
        "Failed to generate reply. Please check your API key and try again."
      );
      setLoading(false);
    }
  };
  
  const generateImage = async (replyText) => {
    setImageLoading(true);
    
    try {
      const imagePrompt = `
        Create a crypto meme image related to this text: "${replyText}"
        
        The image should be suitable for crypto Twitter, including elements like:
        - Bull markets, moon rockets, or crypto symbols
        - If it mentions ${targetCoin}, include ape imagery
        - Cryptocurrency symbols or charts
        - Busta Rhymes reference if mentioned
        - Make it eye-catching and slightly absurd in the style of crypto Twitter memes
      `;
      
      const response = await axios.post(
        "https://api.openai.com/v1/images/generations",
        {
          prompt: imagePrompt,
          n: 1,
          size: "512x512",
          response_format: "url"
        },
        {
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${process.env.REACT_APP_OPENAI_API_KEY}`
          }
        }
      );
      
      const generatedImageUrl = response.data.data[0].url;
      setGeneratedImage(generatedImageUrl);
      
      // Add the new reply to the list
      const newReply = {
        id: Date.now(),
        originalTweet: tweetContent,
        image: generatedImageUrl,
        replyText: generatedReply,
        likes: Math.floor(Math.random() * 1000),
        comments: Math.floor(Math.random() * 200),
        shares: Math.floor(Math.random() * 300)
      };
      
      setReplies([newReply, ...replies]);
      
      // Clear the inputs after successful generation
      setTweetContent("");
      
    } catch (err) {
      console.error("Error generating image:", err);
      setError(
        err.response?.data?.error?.message || 
        "Failed to generate image. Using default image instead."
      );
      
      // Add the reply with a default image
      const newReply = {
        id: Date.now(),
        originalTweet: tweetContent,
        image: "https://i.imgur.com/31HkF5Q.png", // Default image
        replyText: generatedReply,
        likes: Math.floor(Math.random() * 1000),
        comments: Math.floor(Math.random() * 200),
        shares: Math.floor(Math.random() * 300)
      };
      
      setReplies([newReply, ...replies]);
      setTweetContent("");
      
    } finally {
      setLoading(false);
      setImageLoading(false);
    }
  };

  const handleDownload = (replyText, imageUrl) => {
    // Download the reply text
    const textBlob = new Blob([replyText], { type: "text/plain;charset=utf-8" });
    saveAs(textBlob, "Crypto_Reply.txt");
    
    // Download the image if available
    if (imageUrl && imageUrl !== "https://i.imgur.com/31HkF5Q.png") {
      fetch(imageUrl)
        .then(response => response.blob())
        .then(blob => {
          saveAs(blob, "Crypto_Reply_Image.png");
        })
        .catch(err => console.error("Failed to download image:", err));
    }
  };
  
  const handleUpgradeClose = () => {
    setShowUpgradeDialog(false);
  };

  // For the specific example mentioned in requirements
  const handleBustaExample = () => {
    setTweetContent("WE BUYIN THIS DIP?");
    setTargetCoin("APEOUT");
  };

  return (
    <ThemeProvider theme={theme}>
      <Box 
        sx={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #121212 0%, #2D2D2D 100%)',
          py: 6,
        }}
      >
        <Container maxWidth="md">
          {/* Hero Section */}
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                background: 'linear-gradient(90deg, #F7931A, #FFA500)',
                borderRadius: '40px',
                px: 3,
                py: 1,
                mb: 2,
              }}
            >
              <CurrencyBitcoinIcon sx={{ color: 'white', mr: 1 }} />
              <Typography variant="subtitle1" sx={{ color: 'white', fontWeight: 600 }}>
                #CryptoReplyGuy
              </Typography>
            </Box>
            
            <Typography 
              variant="h1" 
              component="h1" 
              sx={{
                fontSize: { xs: '2.5rem', md: '3.5rem' },
                mb: 2,
                background: 'linear-gradient(90deg, #F7931A, #FFA500)',
                backgroundClip: 'text',
                textFillColor: 'transparent',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              CryptoReplyGen<span style={{ color: theme.palette.primary.main }}>.</span>
            </Typography>
            
            <Typography 
              variant="h5" 
              color="textSecondary"
              sx={{ 
                maxWidth: '700px', 
                margin: '0 auto', 
                mb: 4,
                fontWeight: 300
              }}
            >
              Generate <span style={{ fontWeight: 600, color: theme.palette.primary.main }}>hyped crypto replies</span> with 
              AI-generated images to boost your engagement
            </Typography>
            
            <Box 
              sx={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center',
                gap: 1,
                mb: 4,
                flexWrap: 'wrap'
              }}
            >
              <Chip 
                icon={<CurrencyBitcoinIcon fontSize="small" />} 
                label="Bullish replies" 
                sx={{ 
                  borderRadius: '30px',
                  backgroundColor: 'rgba(247, 147, 26, 0.2)',
                  color: theme.palette.primary.main,
                  fontWeight: 600,
                  px: 1,
                  my: 0.5
                }}
              />
              <Chip 
                icon={<MonetizationOnIcon fontSize="small" />} 
                label="AI-powered shilling" 
                sx={{ 
                  borderRadius: '30px',
                  backgroundColor: 'rgba(98, 126, 234, 0.2)',
                  color: theme.palette.secondary.main,
                  fontWeight: 600,
                  px: 1,
                  my: 0.5
                }}
              />
            </Box>
            
            {/* Quick Example Button */}
            <Button
              variant="outlined"
              color="primary"
              onClick={handleBustaExample}
              sx={{ 
                mb: 3,
                borderRadius: '30px',
                borderWidth: 2,
                '&:hover': {
                  borderWidth: 2
                }
              }}
            >
              Try Busta Rhymes Example
            </Button>
          </Box>
          
          {/* Credit Display */}
          <Box 
            sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              mb: 2, 
              alignItems: 'center'
            }}
          >
            <Paper 
              elevation={0} 
              sx={{ 
                px: 3, 
                py: 1, 
                borderRadius: '30px', 
                display: 'inline-flex',
                alignItems: 'center', 
                gap: 1,
                backgroundColor: 'rgba(247, 147, 26, 0.1)',
                border: '1px solid rgba(247, 147, 26, 0.2)'
              }}
            >
              <EmojiEventsIcon color={credits > 0 ? "primary" : "error"} fontSize="small" />
              <Typography 
                variant="body2" 
                color={credits > 0 ? "primary.main" : "error"} 
                fontWeight={600}
              >
                {credits} reply credit{credits !== 1 ? 's' : ''} remaining
              </Typography>
            </Paper>
          </Box>
          
          {/* Reply Generator Card */}
          <Card sx={{ overflow: 'hidden', mb: 4 }}>
            <Box sx={{ p: { xs: 3, md: 4 } }}>
              <Typography variant="h6" fontWeight={600} mb={2}>
                Generate Your Crypto Reply
              </Typography>
              
              <TextField
                fullWidth
                multiline
                rows={3}
                variant="outlined"
                placeholder="Paste the tweet you want to reply to..."
                value={tweetContent}
                onChange={(e) => setTweetContent(e.target.value)}
                sx={{ mb: 3 }}
              />
              
              <TextField
                fullWidth
                variant="outlined"
                placeholder="Coin to shill (default: APEOUT)"
                value={targetCoin}
                onChange={(e) => setTargetCoin(e.target.value)}
                sx={{ mb: 3 }}
              />
              
              <Button 
                onClick={generateReplyWithOpenAI} 
                variant="contained" 
                color="primary" 
                fullWidth
                disabled={loading || imageLoading}
                size="large"
                startIcon={loading || imageLoading ? undefined : <ReplyIcon />}
                sx={{ 
                  py: 1.5,
                  fontWeight: 700,
                  fontSize: '1.1rem',
                  position: 'relative',
                  overflow: 'hidden',
                }}
              >
                {loading ? (
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <CircularProgress size={24} color="inherit" sx={{ mr: 1 }} />
                    Crafting reply...
                  </Box>
                ) : imageLoading ? (
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <CircularProgress size={24} color="inherit" sx={{ mr: 1 }} />
                    Generating image...
                  </Box>
                ) : (
                  "Generate Crypto Reply"
                )}
              </Button>
            </Box>
          </Card>
          
          {/* Error Alert */}
          {error && (
            <Alert 
              severity="error" 
              sx={{ 
                mb: 4,
                borderRadius: theme.shape.borderRadius,
                boxShadow: theme.shadows[2]
              }}
            >
              {error}
            </Alert>
          )}
          
          {/* Replies Feed */}
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', mb: 3, color: 'white' }}>
            {generatedReply ? "Your Generated Replies" : "Trending Crypto Replies 🚀"}
          </Typography>
          
          <Grid container spacing={3}>
            {replies.map((reply) => (
              <Grid item xs={12} key={reply.id}>
                <Card sx={{ mb: 2, borderLeft: `4px solid ${theme.palette.primary.main}` }}>
                  <CardContent>
                    {/* Original Tweet */}
                    <Box sx={{ 
                      p: 2, 
                      mb: 3, 
                      backgroundColor: 'rgba(0,0,0,0.3)', 
                      borderRadius: theme.shape.borderRadius,
                      border: '1px solid rgba(255,255,255,0.1)'
                    }}>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Replying to:
                      </Typography>
                      <Typography variant="body1">
                        {reply.originalTweet}
                      </Typography>
                    </Box>
                    
                    {/* Reply Content */}
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Avatar sx={{ bgcolor: theme.palette.secondary.main, mr: 2 }}>C</Avatar>
                      <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                          CryptoWhale
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          @crypto_gains_247
                        </Typography>
                      </Box>
                    </Box>
                    <Typography variant="body1" paragraph style={{ whiteSpace: 'pre-line' }}>
                      {reply.replyText}
                    </Typography>
                  </CardContent>
                  <CardMedia
                    component="img"
                    height="300"
                    image={reply.image}
                    alt="Crypto meme image"
                    sx={{ objectFit: 'cover' }}
                  />
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <IconButton size="small" color="primary">
                          <CommentIcon fontSize="small" />
                        </IconButton>
                        <Typography variant="body2" color="text.secondary">{reply.comments}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <IconButton 
                          size="small" 
                          color="secondary"
                          onClick={() => handleDownload(reply.replyText, reply.image)}
                        >
                          <DownloadIcon fontSize="small" />
                        </IconButton>
                        <Typography variant="body2" color="text.secondary">{reply.shares}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <IconButton size="small" sx={{ color: '#E94057' }}>
                          <FavoriteIcon fontSize="small" />
                        </IconButton>
                        <Typography variant="body2" color="text.secondary">{reply.likes}</Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
          
          {/* Promo Banner */}
          <Paper
            elevation={0}
            sx={{
              p: 3,
              mt: 4,
              borderRadius: theme.shape.borderRadius,
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 3,
              background: 'linear-gradient(135deg, rgba(247, 147, 26, 0.2) 0%, rgba(98, 126, 234, 0.2) 100%)',
              border: '1px solid rgba(247, 147, 26, 0.3)',
            }}
          >
            <Box sx={{ textAlign: { xs: 'center', md: 'left' } }}>
              <Typography variant="subtitle2" color="primary" fontWeight={600} gutterBottom>
                BOOST YOUR CRYPTO INFLUENCE
              </Typography>
              <Typography variant="h5" fontWeight={700} gutterBottom sx={{ color: 'white' }}>
                Become a Crypto Twitter Master
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Premium users get 10x more engagement on their crypto tweets
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Just buy and hold $BNNA
              </Typography>
            </Box>
            
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={() => setShowUpgradeDialog(true)}
            >
              Upgrade to Premium
            </Button>
          </Paper>
        </Container>
      </Box>
      
      {/* Upgrade Dialog */}
      <Dialog 
        open={showUpgradeDialog} 
        onClose={handleUpgradeClose}
        PaperProps={{
          sx: { 
            borderRadius: theme.shape.borderRadius,
            backgroundColor: '#1E1E1E',
            color: 'white'
          }
        }}
      >
        <DialogTitle sx={{ textAlign: 'center', pt: 4 }}>
          <Typography variant="h5" fontWeight={700}>
            You're out of reply credits
          </Typography>
        </DialogTitle>
        
        <DialogContent sx={{ px: 4, pb: 3 }}>
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <CurrencyBitcoinIcon sx={{ fontSize: 60, color: theme.palette.primary.main, mb: 2 }} />
            <Typography>
              Upgrade to premium for unlimited reply generation and exclusive crypto meme templates!
            </Typography>
          </Box>
          
          <Box sx={{ 
            backgroundColor: 'rgba(247, 147, 26, 0.1)', 
            p: 2, 
            borderRadius: theme.shape.borderRadius,
            mb: 3
          }}>
            <Typography variant="subtitle2" gutterBottom>
              Premium benefits:
            </Typography>
            <Box component="ul" sx={{ pl: 2, mb: 0, mt: 1 }}>
              <Typography component="li">Unlimited reply generations</Typography>
              <Typography component="li">Higher quality AI images</Typography>
              <Typography component="li">Multi-coin shilling in one reply</Typography>
              <Typography component="li">Auto-post to Twitter</Typography>
            </Box>
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ p: 3, pt: 0, flexDirection: 'column', gap: 2 }}>
          <Button 
            target="_blank"
            variant="contained" 
            color="primary"
            fullWidth
            size="large"
            href="https://dexscreener.com/solana/g3rhsoewmmvembdkbec6sfwu91rbmkdyeztwdnmawjxb"
          >
            Upgrade to Premium
          </Button>
          <Button 
            onClick={handleUpgradeClose} 
            color="inherit"
            sx={{ textDecoration: 'underline' }}
          >
            Maybe Later
          </Button>
        </DialogActions>
      </Dialog>
    </ThemeProvider>
  );
}